import { DataPlatformAudienceRule } from '../data-platform.model';

/**
 * Content interest is one criterion in the UI and one or two rules on the wire:
 * an affinity share (percentage of the visitor's engagement within the same
 * dimension type) plus an engagement-score floor, ANDed. Without the floor, one
 * page view reads as 100% affinity.
 */

export type DimensionStrength = 'never' | 'any' | 'leans' | 'dominant' | 'custom';

/** A meaningful share depends on how many distinct values a type has: gender
 *  has two or three, category is a deep breadcrumb path. */
export type DimensionCardinality = 'few' | 'many';

export const DIMENSION_CARDINALITY: Readonly<Record<string, DimensionCardinality>> = {
  pageType: 'few',
  gender: 'few',
  color: 'few',
  size: 'few',
  material: 'few',
  category: 'many',
  brand: 'many',
  tags: 'many',
  author: 'many',
};

export interface DimensionStrengthPreset {
  strength: Exclude<DimensionStrength, 'custom'>;
  labelKey: string;
  descKey: string;
  /** Affinity threshold per cardinality class. Null emits no affinity rule. */
  share: Readonly<Record<DimensionCardinality, number>> | null;
  /** `lt 1` is the negative rung: no recorded engagement for this value. */
  floorOperator: 'gte' | 'lt';
  floorValue: number;
}

export const DIMENSION_STRENGTHS: readonly DimensionStrengthPreset[] = [
  {
    strength: 'never',
    labelKey: 'data-platform.strength-never',
    descKey: 'data-platform.strength-never-desc',
    share: null,
    floorOperator: 'lt',
    floorValue: 1,
  },
  {
    strength: 'any',
    labelKey: 'data-platform.strength-any',
    descKey: 'data-platform.strength-any-desc',
    share: null,
    floorOperator: 'gte',
    floorValue: 25,
  },
  {
    strength: 'leans',
    labelKey: 'data-platform.strength-leans',
    descKey: 'data-platform.strength-leans-desc',
    share: { few: 50, many: 25 },
    floorOperator: 'gte',
    floorValue: 25,
  },
  {
    strength: 'dominant',
    labelKey: 'data-platform.strength-dominant',
    descKey: 'data-platform.strength-dominant-desc',
    share: { few: 75, many: 40 },
    floorOperator: 'gte',
    floorValue: 60,
  },
];

export const CUSTOM_STRENGTH_LABEL_KEY = 'data-platform.strength-custom';
export const CUSTOM_STRENGTH_DESC_KEY = 'data-platform.strength-custom-desc';

export const DEFAULT_DIMENSION_FLOOR = 25;

export const DEFAULT_DIMENSION_STRENGTH: DimensionStrength = 'any';

export function dimensionCardinality(dimension: string | null | undefined): DimensionCardinality {
  const type = (dimension ?? '').split(':')[0];
  return DIMENSION_CARDINALITY[type] ?? 'many';
}

export function findStrengthPreset(strength: DimensionStrength): DimensionStrengthPreset | undefined {
  return DIMENSION_STRENGTHS.find((p) => p.strength === strength);
}

export function strengthShare(preset: DimensionStrengthPreset, dimension: string | null | undefined): number | null {
  return preset.share ? preset.share[dimensionCardinality(dimension)] : null;
}

export interface CustomStrengthValues {
  share?: number | null;
  floor?: number | null;
}

/**
 * Affinity first, so a pair keeps a known order. Without a `type:value` this
 * emits an inert rule rather than the negative shape, which on an empty key
 * reads as `dimension_counts[''] < 1` and matches every profile on the site.
 */
export function strengthToRules(
  strength: DimensionStrength,
  dimension: string,
  custom?: CustomStrengthValues,
): DataPlatformAudienceRule[] {
  const inert: DataPlatformAudienceRule = {
    type: 'dimensions',
    operator: 'gte',
    value: DEFAULT_DIMENSION_FLOOR,
  };
  if (!dimension.includes(':')) {
    if (dimension) {
      inert.dimension = dimension;
    }
    return [inert];
  }

  const preset = findStrengthPreset(strength);
  const share = strength === 'custom' ? (custom?.share ?? null) : preset ? strengthShare(preset, dimension) : null;
  const floorValue = strength === 'custom' ? (custom?.floor ?? null) : (preset?.floorValue ?? null);
  const floorOperator = strength === 'custom' ? 'gte' : (preset?.floorOperator ?? 'gte');

  const rules: DataPlatformAudienceRule[] = [];
  if (share != null) {
    rules.push({ type: 'dimension_affinity', dimension, operator: 'gte', value: share });
  }
  if (floorValue != null) {
    rules.push({ type: 'dimensions', dimension, operator: floorOperator, value: floorValue });
  }
  if (!rules.length) {
    rules.push({ ...inert, dimension });
  }
  return rules;
}

/** True when these two rules are the two halves of one criterion. */
export function isDimensionPair(
  affinity: DataPlatformAudienceRule | undefined,
  floor: DataPlatformAudienceRule | undefined,
): boolean {
  return (
    !!affinity &&
    !!floor &&
    affinity.type === 'dimension_affinity' &&
    floor.type === 'dimensions' &&
    !!affinity.dimension &&
    affinity.dimension === floor.dimension &&
    (affinity.operator ?? 'gte') === 'gte' &&
    (floor.operator ?? 'gte') === 'gte' &&
    affinity.upperValue == null &&
    floor.upperValue == null
  );
}

/** Exact match against a preset for this cardinality class, or Custom. Never
 *  rewrites a threshold: that would silently reshape a saved audience. */
export function rulesToStrength(
  affinity: DataPlatformAudienceRule | undefined,
  floor: DataPlatformAudienceRule | undefined,
): DimensionStrength {
  const dimension = affinity?.dimension ?? floor?.dimension;
  const share = affinity?.value ?? null;
  const floorOperator = floor?.operator ?? 'gte';
  const floorValue = floor?.value ?? null;

  for (const preset of DIMENSION_STRENGTHS) {
    if (strengthShare(preset, dimension) !== share) {
      continue;
    }
    if (preset.floorOperator !== floorOperator || preset.floorValue !== floorValue) {
      continue;
    }
    return preset.strength;
  }
  return 'custom';
}

/** Moves recognised pairs adjacent so the builder can render one row by index.
 *  Safe to reorder: profile rules always AND. */
export function collapseDimensionPairs(rules: readonly DataPlatformAudienceRule[]): DataPlatformAudienceRule[] {
  const floorFor = new Map<number, number>();
  const claimed = new Set<number>();

  rules.forEach((rule, i) => {
    if (rule.type !== 'dimension_affinity' || claimed.has(i)) {
      return;
    }
    const floor = rules.findIndex((cand, j) => j !== i && !claimed.has(j) && isDimensionPair(rule, cand));
    if (floor < 0) {
      return;
    }
    claimed.add(i);
    claimed.add(floor);
    floorFor.set(i, floor);
  });

  const partners = new Set(floorFor.values());
  const out: DataPlatformAudienceRule[] = [];
  rules.forEach((rule, i) => {
    const floor = floorFor.get(i);
    if (floor != null) {
      out.push(rule, rules[floor]);
      return;
    }
    if (!partners.has(i)) {
      out.push(rule);
    }
  });
  return out;
}

export interface DimensionRow {
  affinity: DataPlatformAudienceRule | undefined;
  floor: DataPlatformAudienceRule | undefined;
  /** How many entries of the rules array this one criterion occupies. */
  span: 1 | 2;
}

/** Reads the criterion starting at `index` out of a collapsed rules array. */
export function dimensionRowAt(rules: readonly DataPlatformAudienceRule[], index: number): DimensionRow | null {
  const rule = rules[index];
  if (!rule) {
    return null;
  }
  if (rule.type === 'dimension_affinity') {
    const next = rules[index + 1];
    return isDimensionPair(rule, next)
      ? { affinity: rule, floor: next, span: 2 }
      : { affinity: rule, floor: undefined, span: 1 };
  }
  if (rule.type === 'dimensions') {
    return { affinity: undefined, floor: rule, span: 1 };
  }
  return null;
}

export interface DimensionHint {
  key: string;
  params: Record<string, string | number>;
}

/** Built from the resolved values, so it cannot drift from what gets saved. */
export function dimensionHint(
  dimensionTypeLabel: string,
  dimensionValue: string,
  share: number | null,
  floor: number | null,
  floorOperator: 'gte' | 'lt',
): DimensionHint | null {
  if (!dimensionValue) {
    return null;
  }
  const params = {
    type: dimensionTypeLabel.toLowerCase(),
    value: dimensionValue,
    share: share ?? 0,
    floor: floor ?? 0,
  };

  if (floorOperator === 'lt') {
    return { key: 'data-platform.strength-hint-never', params };
  }
  if (share != null && floor != null) {
    return { key: 'data-platform.strength-hint-share', params };
  }
  if (share != null) {
    return { key: 'data-platform.strength-hint-share-only', params };
  }
  if (floor != null) {
    return {
      key: floor === DEFAULT_DIMENSION_FLOOR ? 'data-platform.strength-hint-any' : 'data-platform.strength-hint-floor',
      params,
    };
  }
  return null;
}
